import { computed, defineAsyncComponent, defineComponent, onMounted, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { AccountAction, AccountGetter } from '@/store/account/account.module';
import { Account } from '@/store/account/account.model';
import { Role } from '@/store/role/role.model';
import { AssignmentAction, AssignmentGetter } from '@/store/assignment/assignment.module';
import { Assignment } from '@/store/assignment/assignment.model';
import { FiscalYearAction, FiscalYearGetter } from '@/store/fiscalYear/fiscalYear.module';
import { FiscalYear } from '@/store/fiscalYear/fiscalYear.model';
import { RegionAction, RegionGetter } from '@/store/region/region.module';
import { Region } from '@/store/region/region.model';
import { ProjectResultAction, ProjectResultGetter } from '@/store/projectResult/projectResult.module';
import { ProjectRegionResult, ProjectRegionResultItem } from '@/store/projectResult/projectResult.model';
import { Province } from '@/store/province/province.model';
import LoadingComponent from '@/components/loading/Loading.vue';

export default defineComponent({
  name: 'CreateProjectPage',
  components: {
    AsyncCreateProjectTableComponent: defineAsyncComponent(() => import('@/components/project/create/createTable/CreateTable.vue')),
    AsyncCreateProjectWizardComponent: defineAsyncComponent(() => import('@/components/project/create/createWizard/CreateWizard.vue')),
    LoadingComponent
  },
  setup: () => {
    const store = useStore();
    const account = computed<Account>(() => store.getters[AccountGetter.ACCOUNT]);
    const role = computed<Role | undefined>(() => store.getters[AccountGetter.SINGLE_ROLE]);
    const assignment = computed<Assignment | undefined>(() => store.getters[AssignmentGetter.BY_ID](account.value?.id));
    const isLoading = computed(() => !isOrs.value && (store.getters[AssignmentGetter.STATUS] === 'init' || store.getters[AssignmentGetter.STATUS] === 'loading'));

    const selectedFiscalYear = ref<FiscalYear | null>();
    const fiscalYears = computed<FiscalYear[]>(() => {
      const all = store.getters[FiscalYearGetter.ALL] as FiscalYear[];
      //return all.filter(m => m.status >= 0).sort(m => m.status);
      return all.slice(0, 3);
    });
    const selectedRegion = ref<Region | null>();
    const regions = computed<Region[]>(() => {
      const all = store.getters[RegionGetter.ALL] as Region[];
      return all.filter(m => m.id !== 1);
    });
    const provinces = computed<ProjectRegionResultItem[]>(() => {
      const result = store.getters[ProjectResultGetter.REGION_RESULT] as ProjectRegionResult;
      return result?.provinces;
    });
    const selectedProvince = ref<Province | null>();
    const isOrs = computed<boolean>(() => store.getters[AccountGetter.IS_ROLE]('เจ้าหน้าที่ ส.กกภ.'));
    const assigned = computed(() => isOrs.value || !!assignment.value);
    const createMode = ref(false);
    const projectComponent = computed(() => `async-create-project-${createMode.value ? 'wizard' : 'table'}-component`);
    const isProvinceLoading = computed(() => store.getters[ProjectResultGetter.STATUS] === 'init' || store.getters[ProjectResultGetter.STATUS] === 'loading');

    onMounted(() => {
      store.dispatch(AccountAction.GET)
        .then(() => store.dispatch(FiscalYearAction.FETCH)
          .then(() => selectedFiscalYear.value = fiscalYears.value[0]))
          .then(() => {
            switch (role.value?.name) {
              default:
                store.dispatch(RegionAction.FETCH)
                  .then(() => selectedRegion.value = regions.value[0])
                  .then(() => projectRegion());
                break;
              case 'ผู้ช่วยผู้ตรวจราชการ':
                store.dispatch(AssignmentAction.FIND, { mode: 'REGION', id: account.value?.id });
                break;
            }
          });
    });

    watch(() => selectedFiscalYear.value, (fiscalYear) => {
      if (assignment.value && role.value?.name === 'ผู้ช่วยผู้ตรวจราชการ') {
          store.dispatch(RegionAction.FETCH_REGION, { fiscalYearId: fiscalYear?.id, userId: account.value.id })
            .then(() => selectedRegion.value = regions.value[0])
            .then(() => projectRegion());
      }
    });

    const toggle = () => {
      createMode.value = !createMode.value;
    };

    const filterChange = () => {
      store.dispatch(ProjectResultAction.GET_REGION_RESULT, {
        fiscalYearId: selectedFiscalYear.value?.id,
        regionId: selectedRegion.value?.id,
      }).then(() => {
        setTimeout(() => selectedProvince.value = (provinces.value) ? provinces.value[0] : null, 200);
        createMode.value = false;
      });
    };

    const provinceChange = (province: Province) => {
      selectedProvince.value = province;
      createMode.value = false;
    };

    const projectRegion = () => {
      store.dispatch(ProjectResultAction.GET_REGION_RESULT, {
        fiscalYearId: selectedFiscalYear.value?.id,
        regionId: selectedRegion.value?.id
      })
        .then(() => selectedProvince.value = (provinces.value) ? provinces.value[0] : null,);
    };

    return {
      projectComponent,
      assigned,
      isLoading,
      fiscalYears,
      selectedFiscalYear,
      regions,
      selectedRegion,
      provinces,
      selectedProvince,
      isProvinceLoading,
      toggle,
      filterChange,
      provinceChange,
      projectRegion,
    };
  },
});
